/// <reference types='@types/google.maps' />
import { Component, ElementRef, Input, OnChanges, OnInit, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl, SafeUrl, SafeHtml } from '@angular/platform-browser';
import { MensagemService, ProgressoService } from 'maestro-ng';
import { CategoriaArquivo } from '../../models/categoria-arquivo.enum';
import { TipoArquivo } from '../../models/tipo-arquivo.enum';
import { blobToBase64, blobToString, downloadBlob } from '../../util/utils';
import { ArquivoVisualizavel } from './arquivo-visualizavel.model';
import { RTFJS } from 'rtf.js';

declare let geoXML3: any;

@Component({
  selector: 'app-arquivo-visualizador',
  templateUrl: './arquivo-visualizador.component.html',
  styleUrls: ['./arquivo-visualizador.component.css'],
})
export class ArquivoVisualizadorComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('gmapViewer')
  public gmapViewer: ElementRef;

  @Input()
  public arquivo: ArquivoVisualizavel;

  public texto: string;
  public base64: SafeUrl;
  public temVisualizador: boolean;
  public resultadoHTML: SafeHtml[];

  public pdfUrl: SafeResourceUrl;
  private blobUrl: string;

  constructor(
    private mensagemService: MensagemService,
    private progressoService: ProgressoService,
    private domSanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {}

  public ngOnChanges(_: SimpleChanges) {
    if (this.arquivo !== undefined) {
      switch (this.arquivo.categoria) {
        case CategoriaArquivo.AUDIO:
          this.processarCategoriaAudio();
          break;

        case CategoriaArquivo.IMAGEM:
          this.processarCategoriaImagem();
          break;

        case CategoriaArquivo.VIDEO:
          this.processarCategoriaVideo();
          break;

        case CategoriaArquivo.DOCUMENTO:
          this.processarCategoriaDocumento();
          break;

        case CategoriaArquivo.GEOGRAFICO:
          this.processarCategoriaGeografico();
          break;

        case CategoriaArquivo.OUTROS:
        default:
          this.temVisualizador = false;
          break;
      }
    }
  }

  public ngOnDestroy() {
    if (this.blobUrl) {
      URL.revokeObjectURL(this.blobUrl);
    }
  }

  public processarCategoriaDocumento(): void {
    switch (this.arquivo.tipo) {
      case TipoArquivo.TEXT_HTML:
      case TipoArquivo.TEXT_PLAIN:
        this.recuperarArquivoEmString(this.arquivo.conteudo)
          .then((texto) => {
            this.texto = texto;
            this.progressoService.fechar();
          })
          .catch((response) => {
            this.progressoService.fechar();
            this.mensagemService.addErro('Erro ao carregar o arquivo de texto!', response);
          });
        this.temVisualizador = true;
        break;

      case TipoArquivo.APPLICATION_PDF:
        this.temVisualizador = true;
        this.criarPdfUrl();
        this.progressoService.fechar();
        break;

      case TipoArquivo.TEXT_RICHTEXT:
        this.temVisualizador = true;
        this.processarArquivoRTF();
        break;

      default:
        this.temVisualizador = false;
        break;
    }
  }

  private criarPdfUrl() {
    const blob = new Blob([this.arquivo.conteudo], { type: 'application/pdf' });
    this.blobUrl = URL.createObjectURL(blob);
    this.pdfUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(this.blobUrl);
  }

  public processarCategoriaGeografico(): void {
    this.recuperarArquivoEmString(this.arquivo.conteudo)
      .then((texto) => {
        const map = new google.maps.Map(this.gmapViewer.nativeElement);
        const parser = new geoXML3.parser({ map: map });
        parser.parseKmlString(texto);
        setTimeout(() => {
          map.setZoom(14);
        }, 300);
        this.progressoService.fechar();
      })
      .catch((response) => {
        this.progressoService.fechar();
        this.mensagemService.addErro('Erro ao carregar o arquivo geográfico!', response);
      });
    this.temVisualizador = true;
  }

  public processarCategoriaImagem(): void {
    this.recuperarArquivoEmBase64(this.arquivo)
      .then((base64) => {
        this.base64 = base64;
        this.progressoService.fechar();
      })
      .catch((response) => {
        this.progressoService.fechar();
        this.mensagemService.addErro('Erro ao carregar o arquivo de imagem!', response);
      });
    this.temVisualizador = true;
  }

  public processarCategoriaAudio(): void {
    this.recuperarArquivoEmBase64(this.arquivo)
      .then((base64) => {
        this.base64 = base64;
        this.progressoService.fechar();
      })
      .catch((response) => {
        this.progressoService.fechar();
        this.mensagemService.addErro('Erro ao carregar o arquivo de áudio!', response);
      });
    this.temVisualizador = true;
  }

  public processarCategoriaVideo(): void {
    this.recuperarArquivoEmBase64(this.arquivo)
      .then((base64) => {
        this.base64 = base64;
        this.progressoService.fechar();
      })
      .catch((response) => {
        this.progressoService.fechar();
        this.mensagemService.addErro('Erro ao carregar o arquivo de vídeo!', response);
      });
    this.temVisualizador = true;
  }

  public recuperarArquivoEmString(blob: Blob): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      blobToString(blob)
        .then((texto) => resolve(texto))
        .catch((response) => reject(response.error));
    });
  }

  public recuperarArquivoEmBase64(arquivo: ArquivoVisualizavel): Promise<SafeResourceUrl> {
    return new Promise<SafeResourceUrl>((resolve, reject) => {
      blobToBase64(arquivo.conteudo)
        .then((base64) => {
          if (arquivo.tipo !== TipoArquivo.NAO_IDENTIFICADO) {
            base64 = base64.replace('application/octet-stream', this.arquivo.getContentType());
          }
          resolve(this.domSanitizer.bypassSecurityTrustUrl(base64));
        })
        .catch((response) => reject(response.error));
    });
  }

  public download(): void {
    downloadBlob(this.arquivo.nome, this.arquivo.conteudo);
  }

  public processarArquivoRTF() {
    RTFJS.loggingEnabled(false);

    const reader = new FileReader();
    reader.onload = () => {
      const rtfArrayBuffer = reader.result as ArrayBuffer;
      const doc = new RTFJS.Document(rtfArrayBuffer, null);

      doc
        .render()
        .then((elementosHtml) => {
          const elementosSeguros = elementosHtml.map((elemento) => this.domSanitizer.bypassSecurityTrustHtml(elemento.innerHTML));
          this.resultadoHTML = elementosSeguros;
        })
        .catch((erro) => console.error(erro));
    };

    reader.readAsArrayBuffer(this.arquivo.conteudo);
  }
}
